import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';

import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildPieCompositionOption, buildPieCompositionTree } from './unified-pie-composition-option.builder';
import type { PcNode, PieCompositionWidgetData } from './unified-pie-composition-chart.types';

/** Depth-first search for `name` anywhere in the hierarchy, returning the
 *  full path from a top-level node down to the match (inclusive) so a click
 *  several levels deep (Sunburst/Treemap) extends the breadcrumb correctly
 *  in one step. */
function findNodePath(nodes: PcNode[], name: string): PcNode[] | null {
  for (const node of nodes) {
    if (node.name === name) return [node];
    if (node.children?.length) {
      const sub = findNodePath(node.children, name);
      if (sub) return [node, ...sub];
    }
  }
  return null;
}

const GAUGE_VARIANTS = new Set(['radial-progress', 'circular-progress', 'ring-progress']);

/**
 * Unified Pie & Composition chart — ONE ECharts component for the whole
 * family (Pie / Donut / Semi Donut / Sunburst / Treemap / Nested Donut /
 * Radial, Circular & Ring Progress). All variation lives in
 * `data.capabilities` (PieCompositionCapabilities); the PURE
 * `buildPieCompositionOption` turns capabilities + a resolved hierarchy into
 * the option and this component is just the lifecycle shell (init / dispose /
 * resize / theme / drill-down), mirroring the unified Scatter & Bubble
 * component so the format popup, cross-filter registry and published
 * renderer reuse identically.
 */
@Component({
  selector: 'app-unified-pie-composition-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './unified-pie-composition-chart.component.html',
  styleUrl: './unified-pie-composition-chart.component.scss',
})
export class UnifiedPieCompositionChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: PieCompositionWidgetData | Record<string, unknown>;

  /** Emitted on a leaf click when `capabilities.emitSelection` is on. */
  @Output() selection = new EventEmitter<{ category: string; value: number }>();

  /** Emitted on a leaf click when `capabilities.crossFilter` is on — the
   *  column + the FULL current selection (multi-select). Plugs into the DVM
   *  cross-filter. */
  @Output() crossFilterChange = new EventEmitter<{ column: string; value: string | null; values: string[] }>();

  protected myChart!: echarts.ECharts;
  private crossFilterValues = new Set<string>();

  /** Root of the hierarchy resolved from the CURRENT rows (rebuilt whenever
   *  rows change; drilling never rebuilds it, only re-slices it). */
  private rootTree: PcNode[] = [];
  /** Breadcrumb stack of drilled-into nodes — empty = viewing the root. */
  drillPath: PcNode[] = [];

  @ViewChild('pcHost', { static: false }) private hostRef?: ElementRef<HTMLDivElement>;

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private lastRows: Array<Record<string, unknown>> = [];

  private get cfg(): PieCompositionWidgetData {
    return (this.data || {}) as PieCompositionWidgetData;
  }

  /** CSS height for the host div (coerce raw numbers to px like the Bar/Scatter shells). */
  get hostHeight(): string {
    const h = this.cfg.height;
    if (h === undefined || h === null || h === '') return 'var(--dvm-chart-height, 300px)';
    if (typeof h === 'number') return `${h}px`;
    return /^\d+$/.test(String(h)) ? `${h}px` : String(h);
  }

  get rootLabel(): string {
    return this.cfg.title || 'All';
  }

  /** The level the chart currently renders — the root, or a drilled-into
   *  node's children. */
  get currentNodes(): PcNode[] {
    return this.drillPath.length ? (this.drillPath[this.drillPath.length - 1].children ?? []) : this.rootTree;
  }

  private get isGaugeVariant(): boolean {
    const cap = this.cfg.capabilities;
    if (!cap) return false;
    return cap.variantFlags ? cap.variantFlags.gauge === true : GAUGE_VARIANTS.has(cap.variant);
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => [],
    });

    const staticRows = this.resolveStaticRows();
    if (staticRows) {
      setTimeout(() => this.renderRows(staticRows), 0);
    } else {
      setTimeout(() => this.loadAndRenderChart(initialFilters ?? undefined), 0);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    const host = this.resolveHost();
    if (host && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
      this.resizeObserver.observe(host);
    }

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.myChart || !this.lastRows.length) return;
      const chartDom = this.resolveHost();
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderCurrentLevel();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].isFirstChange()) return;
    if (!this.myChart) return;
    const staticRows = this.resolveStaticRows();
    this.renderRows(staticRows ?? this.lastRows);
  }

  private resolveHost(): HTMLElement | null {
    return this.hostRef?.nativeElement || document.getElementById(this.cfg.widgetId || '');
  }

  private resolveStaticRows(): Array<Record<string, unknown>> | null {
    const candidate = this.cfg.staticData ?? this.cfg.rows ?? this.cfg.data;
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown })?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : null;
  }

  protected loadAndRenderChart(filters?: unknown): void {
    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    const staticRows = this.resolveStaticRows();
    if (staticRows) { this.renderRows(staticRows); return; }
    if (!this.cfg.apiUrl) { this.renderRows([]); return; }

    this.myChart.showLoading();
    this.configService.getConfig(this.cfg.apiUrl, this.data, filters)?.subscribe({
      next: (res: unknown) => {
        const raw = (res as { data?: unknown })?.data ?? res;
        this.renderRows(Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : []);
        this.myChart.hideLoading();
      },
      error: () => this.myChart.hideLoading(),
    });
  }

  /** Rebuild the hierarchy from fresh rows. A new row set can no longer be
   *  trusted to contain whatever the user had drilled into, so drill state
   *  always resets to the root here — only `drillTo`/click-to-drill move it
   *  after that. */
  private renderRows(rows: Array<Record<string, unknown>>): void {
    const capabilities = this.cfg.capabilities;
    if (!capabilities) return;
    this.lastRows = rows;
    this.rootTree = buildPieCompositionTree(rows, capabilities);
    this.drillPath = [];
    this.renderCurrentLevel();
  }

  /** PURE re-slice of the already-built hierarchy — no refetch. Called on
   *  initial render, drill-in, drill-out (breadcrumb), and theme changes. */
  private renderCurrentLevel(): void {
    const capabilities = this.cfg.capabilities;
    if (!capabilities) return;

    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    const option = buildPieCompositionOption(this.currentNodes, capabilities) as Record<string, unknown>;
    applyRoleVisibilityToOption(option, this.data);
    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.myChart.resize();
    setTimeout(() => this.myChart?.resize(), 60);
    this.addResizeListener();
    this.wrapper.afterRender();
    this.setupInteractions();
  }

  /** Breadcrumb chip click: `-1` returns to the root, otherwise truncates the
   *  path to (and including) the clicked chip. */
  drillTo(index: number): void {
    this.drillPath = index < 0 ? [] : this.drillPath.slice(0, index + 1);
    this.renderCurrentLevel();
  }

  /** Single click handler drives BOTH drill-down and leaf selection/cross-
   *  filter — a parent-node click descends, a leaf click publishes. Sunburst/
   *  Treemap disable their own native zoom/breadcrumb (`nodeClick:false`,
   *  `breadcrumb:{show:false}` in the option builder) so this is the ONLY
   *  drill mechanism across every hierarchy-capable variant. */
  private setupInteractions(): void {
    const cap = this.cfg.capabilities;
    if (!cap || !this.myChart) return;

    // ChartWrapperService's own `legendselectchanged` handler (wired up by
    // `wrapper.afterRender()`, called just before this) either applies its
    // series-selection logic (irrelevant here — this family has one series)
    // or blocks the click outright when `enableLegendClick` isn't set. Drop
    // it so ECharts' own native multi-toggle runs unopposed — same as the
    // Scatter & Bubble family: each legend entry hides/shows independently.
    this.myChart.off('legendselectchanged');

    this.myChart.off('click');

    this.myChart.on('click', (params: { data?: unknown; name?: string }) => {
      const dataObj = params.data as { name?: string; value?: unknown } | null | undefined;
      const name = String(dataObj?.name ?? params.name ?? '');
      if (!name) return;
      const path = findNodePath(this.currentNodes, name);
      const node = path ? path[path.length - 1] : undefined;

      if (!this.isGaugeVariant && cap.drillDownEnabled !== false && node?.children?.length) {
        this.drillPath = [...this.drillPath, ...(path as PcNode[])];
        this.renderCurrentLevel();
        return;
      }

      const value = node?.value ?? 0;
      if (cap.emitSelection) {
        this.selection.emit({ category: name, value });
      }
      if (cap.crossFilter) {
        const column = cap.crossFilterKey || cap.categoryField;
        if (!column) return;
        if (this.crossFilterValues.has(name)) this.crossFilterValues.delete(name);
        else this.crossFilterValues.add(name);
        const values = Array.from(this.crossFilterValues);
        this.crossFilterChange.emit({ column, value: name, values });
      }
    });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
